import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { USERAXIOS } from "../../api";

interface User {
  id: number;
  name: string;
  email: string;
  account_type: string;
  user_name: string;
}

interface UsersResponse {
  data?: User[];
  meta?: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export const useUsers = (accountType?: string) => {
  return useQuery({
    queryKey: ["users", accountType],
    queryFn: async (): Promise<User[]> => {
      const url = accountType
        ? `/users/select_list?account_type=${accountType}`
        : "/users/select_list";

      const response = await USERAXIOS.get<UsersResponse | User[]>(url);

      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && response.data.data) {
        return response.data.data;
      }

      return [];
    },
    enabled: true,
  });
};

export const useUsersForAutocomplete = (
  accountType?: string,
  pageSize: number = 20,
  searchTerm: string = ""
) => {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["users", "infinite", accountType, pageSize, searchTerm],
    queryFn: async ({ pageParam = 1 }): Promise<UsersResponse> => {
      const baseUrl = accountType
        ? `/users/select_list?account_type=${accountType}`
        : "/users/select_list";

      const response = await USERAXIOS.get<UsersResponse>(baseUrl, {
        params: {
          page: pageParam,
          per_page: pageSize,
          search: searchTerm || undefined,
        },
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.meta) return undefined;
      return lastPage.meta.current_page < lastPage.meta.last_page
        ? lastPage.meta.current_page + 1
        : undefined;
    },
    initialPageParam: 1,
  });

  // Flatten all pages into a single array
  const users = useMemo(() => {
    return data?.pages.flatMap((page) => page.data || []) || [];
  }, [data]);

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return {
    items: users,
    loading: isLoading || isFetchingNextPage,
    hasMore: hasNextPage,
    loadMore,
    error,
    refetch,
    totalPages: data?.pages[0]?.meta?.last_page || 0,
    totalItems: data?.pages[0]?.meta?.total || 0,
  };
};
